import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle,
  faExclamationCircle,
  faSpinner,
  faClock,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../utils/currencyUtils';
import './PledgeTable.css';

const PledgeTable = ({ pledges, onLoadMore, hasMore, loading, onEdit, onDelete }) => {
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pledgeToDelete, setPledgeToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.dropdown-wrapper')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  // Format amount with currency
  const formatAmount = (amount, currencyCode = 'usd') => {
    const amountCents = Math.round(amount * 100);
    return formatCurrency(amountCents, currencyCode);
  };

  // Format date - use UTC to avoid timezone shifts for DATE fields (YYYY-MM-DD)
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    
    // Check if it's a DATE field (YYYY-MM-DD) or a TIMESTAMP
    if (dateString.includes('T') || dateString.includes(' ')) {
      // TIMESTAMP - parse normally
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else {
      // DATE field - parse as UTC to prevent timezone conversion
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      });
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return {
          icon: faCheckCircle,
          color: '#10b981',
          label: 'Paid',
          bgColor: '#ecfdf5'
        };
      case 'partially_paid':
        return {
          icon: faClock,
          color: '#3b82f6',
          label: 'Partially Paid',
          bgColor: '#eff6ff'
        };
      case 'unpaid':
        return {
          icon: faExclamationCircle,
          color: '#f59e0b',
          label: 'Unpaid',
          bgColor: '#fffbeb'
        };
      case 'overdue':
        return {
          icon: faExclamationCircle,
          color: '#ef4444',
          label: 'Overdue',
          bgColor: '#fef2f2'
        };
      default:
        return {
          icon: faSpinner,
          color: '#64748b',
          label: status || 'Unknown',
          bgColor: '#f8fafc'
        };
    }
  };

  // Render status badge
  const renderStatusBadge = (pledge) => {
    const statusInfo = getStatusInfo(pledge.payment_status);
    return (
      <div 
        className="status-badge" 
        style={{ 
          color: statusInfo.color,
          backgroundColor: statusInfo.bgColor
        }}
      >
        <FontAwesomeIcon 
          icon={statusInfo.icon}
        />
        <span>{statusInfo.label}</span>
      </div>
    );
  };

  // Render member info
  const renderMemberInfo = (pledge) => {
    const member = pledge.native_users;
    
    if (!member) {
      return (
        <div className="member-info">
          <div className="member-name">Unknown Member</div>
          <div className="member-email text-muted">No email</div>
        </div>
      );
    }

    const memberName = member.full_name || 
      (member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : '') ||
      member.name || 
      'Unknown Member';

    return (
      <div className="member-info">
        <div className="member-name">
          {memberName}
        </div>
        {member.email && (
          <div className="member-email text-muted">
            {member.email}
          </div>
        )}
      </div>
    );
  };

  // Render pledge type
  const renderPledgeType = (pledge) => {
    const pledgeType = pledge.pledge_types;

    if (pledgeType && pledgeType.name) {
      return (
        <div 
          className="pledge-type-badge"
          style={{ 
            backgroundColor: `${pledgeType.color}20`,
            color: pledgeType.color,
            border: `1px solid ${pledgeType.color}`
          }}
        >
          {pledgeType.name}
        </div>
      );
    }

    // Fallback: Show "General"
    return (
      <div 
        className="pledge-type-badge"
        style={{ 
          backgroundColor: '#9ca3af20',
          color: '#6b7280',
          border: '1px solid #9ca3af'
        }}
      >
        General
      </div>
    );
  };

  // Render description/notes with expandable functionality
  const renderNotes = (pledge) => {
    const description = pledge.description || pledge.notes;
    
    if (!description || description.trim() === '') {
      return <span className="text-muted">—</span>;
    }

    const maxLength = 60;
    const pledgeId = pledge.id;
    const isExpanded = expandedNotes.has(pledgeId);
    
    // If message is short enough, show it in full
    if (description.length <= maxLength) {
      return <span className="pledge-notes">{description}</span>;
    }

    // For long messages, provide expand/collapse functionality
    const toggleExpanded = (e) => {
      e.stopPropagation();
      const newExpanded = new Set(expandedNotes);
      if (isExpanded) {
        newExpanded.delete(pledgeId);
      } else {
        newExpanded.add(pledgeId);
      }
      setExpandedNotes(newExpanded);
    };

    return (
      <div className="notes-container">
        <span className="pledge-notes">
          {isExpanded ? description : `${description.substring(0, maxLength)}...`}
        </span>
        <button 
          className="notes-toggle"
          onClick={toggleExpanded}
          type="button"
        >
          {isExpanded ? 'see less' : 'see more'}
        </button>
      </div>
    );
  };

  return (
    <div className="pledge-table-container">
      <div className="table-wrapper">
        <table className="pledge-table">
          <thead>
            <tr>
              <th className="col-member">Member</th>
              <th className="col-type">Type</th>
              <th className="col-amount">Amount</th>
              <th className="col-notes">Description</th>
              <th className="col-status">Status</th>
              <th className="col-date">Charge Date</th>
              <th className="col-due-date">Due Date</th>
              <th className="col-notification">Notification</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!pledges || pledges.length === 0 ? (
              <tr className="empty-state-row">
                <td colSpan="9" className="empty-state-cell">
                  No pledges have been created yet.
                </td>
              </tr>
            ) : (
              pledges.map((pledge) => (
                <tr key={pledge.id} className={`pledge-row ${pledge.payment_status?.toLowerCase()}`}>
                  <td className="col-member">
                    {renderMemberInfo(pledge)}
                  </td>
                  
                  <td className="col-type">
                    {renderPledgeType(pledge)}
                  </td>
                  
                  <td className="col-amount">
                    <div className="amount-cell">
                      {formatAmount(pledge.amount, pledge.currency)}
                    </div>
                  </td>
                  
                  <td className="col-notes">
                    {renderNotes(pledge)}
                  </td>
                  
                  <td className="col-status">
                    {renderStatusBadge(pledge)}
                  </td>
                  
                  <td className="col-date">
                    <div className="date-cell">
                      <div className="date-primary">{formatDate(pledge.charge_date)}</div>
                    </div>
                  </td>
                  
                  <td className="col-due-date">
                    <div className="date-cell">
                      <div className="date-primary">{formatDate(pledge.due_date)}</div>
                    </div>
                  </td>
                  
                  <td className="col-notification">
                    <div className="notification-cell">
                      {pledge.notification_sent_at ? (
                        <span className="notification-badge sent">
                          ✓ Sent {formatDate(pledge.notification_sent_at)}
                        </span>
                      ) : (
                        <span className="notification-badge not-sent">
                          — Not Sent
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="col-actions">
                    <div className="action-buttons">
                      <div className="dropdown-wrapper">
                        <button 
                          className="action-menu-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownId(openDropdownId === pledge.id ? null : pledge.id);
                          }}
                          title="More actions"
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                        {openDropdownId === pledge.id && (
                          <div className="action-dropdown">
                            <button
                              className="dropdown-item"
                              disabled={pledge.payment_status === 'paid'}
                              onClick={() => {
                                if (pledge.payment_status !== 'paid') {
                                  onEdit && onEdit(pledge);
                                  setOpenDropdownId(null);
                                }
                              }}
                              title={pledge.payment_status === 'paid' ? "Cannot edit paid pledges" : ""}
                            >
                              Edit
                            </button>
                            <button
                              className="dropdown-item delete-item"
                              disabled={pledge.payment_status === 'paid'}
                              onClick={() => {
                                if (pledge.payment_status !== 'paid') {
                                  setPledgeToDelete(pledge);
                                  setShowDeleteModal(true);
                                  setOpenDropdownId(null);
                                }
                              }}
                              title={pledge.payment_status === 'paid' ? "Cannot delete paid pledges" : ""}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load more button */}
      {hasMore && (
        <div className="table-footer">
          <button 
            className="load-more-button"
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && pledgeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Pledge
              </h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this pledge for <strong>{pledgeToDelete.native_users?.full_name || pledgeToDelete.native_users?.email}</strong>?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="text-sm text-gray-700">
                  <div className="flex justify-between mb-1">
                    <span className="font-medium">Amount:</span>
                    <span>{formatAmount(pledgeToDelete.amount, pledgeToDelete.currency)}</span>
                  </div>
                  {pledgeToDelete.pledge_types && (
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">Type:</span>
                      <span>{pledgeToDelete.pledge_types.name}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <span className="capitalize">{pledgeToDelete.payment_status}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-red-600 mb-6">
                This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end rounded-b-lg">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPledgeToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (onDelete) {
                    onDelete(pledgeToDelete.id);
                  }
                  setShowDeleteModal(false);
                  setPledgeToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete Pledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PledgeTable;
